import React from 'react';
import { useNavigate } from 'react-router-dom';

import { HStack, LookoModalV2, VStack } from '@looko-design-system';
import { ChevronRight } from '@mui/icons-material';
import CalculateIcon from '@mui/icons-material/Calculate';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import { ButtonBase, Divider, IconButton, Typography } from '@mui/material';

import type { 플랜_서비스_타입 } from '@/app/payment/types';
import { useShopInfo2 } from '@/app/shop/shop-info/hooks';
import usePatchShop from '@/app/shop/usePatchShop';
import { AI_상품정보_자동추출_요금_테이블 } from '@/features/플랜/components/AI_상품정보_자동추출_요금_테이블';
import { AI_통합서비스_요금_테이블 } from '@/features/플랜/components/AI_통합서비스_요금_테이블';
import { C쇼핑몰_판매관리_요금_테이블 } from '@/features/플랜/components/쇼핑몰_판매관리_요금_테이블';
import { 플랜_게스트_라우터, 플랜_라우터 } from '@/router/plan/routes';

import CheckCircleIcon from '../../../assets/icon/check-circle-icon.svg';
import { useH_서비스별_요금_가져오기 } from '../hooks/useH_서비스별_요금_가져오기';

export const C플랜_리뉴얼_안내_모달 = React.memo(function C플랜_리뉴얼_안내_모달() {
  const navigate = useNavigate();

  const { shopInfo } = useShopInfo2();
  const { mutate: patchShop } = usePatchShop();

  const 서비스_목록 = useH_서비스_목록();
  const { 기본서버이용료 } = useH_서비스별_요금_가져오기();

  const 리뉴얼_전_플랜 = React.useMemo(() => {
    if (shopInfo.servicePricingCodes?.length === 1) {
      return '기본 플랜';
    }
    if (shopInfo.servicePricingCodes?.length === 2) {
      return '프리미엄 플랜';
    }
    return '무료 플랜';
  }, [shopInfo.servicePricingCodes]);

  const closeModal = () => {
    patchShop({
      shopID: shopInfo.shopID,
      body: {
        servicePricingAgreed: true,
      },
    });
  };

  const handleSubmit = () => {
    closeModal();
    navigate(플랜_라우터.플랜요금.path);
  };

  return (
    <LookoModalV2 open={!shopInfo.servicePricingAgreed}>
      <LookoModalV2.Header>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant="H2_Looko">서비스 신청</Typography>
          <IconButton onClick={closeModal} size="small">
            <ClearOutlined />
          </IconButton>
        </HStack>
      </LookoModalV2.Header>
      <LookoModalV2.Content>
        <VStack width={1040} gap={5}>
          <VStack p={20 / 8} gap={2} borderRadius={'16px'} bgcolor={'rgba(237, 108, 2, 0.04)'}>
            <HStack gap={4 / 8} alignItems={'center'}>
              <ErrorIcon fontSize="small" sx={{ color: '#E65100' }} />
              <Typography variant="H2_Looko" color="#E65100">
                {shopInfo.shopName} 샵은 2024년 12월 기준, {리뉴얼_전_플랜}을 이용하셨습니다
              </Typography>
            </HStack>

            <Typography fontSize={16} fontWeight={400} color={'#E65100'} whiteSpace={'pre-line'}>
              {리뉴얼_전_플랜 === '프리미엄 플랜'
                ? `2025년부터 프리미엄 플랜은 AI 통합 서비스 및 쇼핑몰 판매 관리 서비스로 변경되었습니다. 
서비스를 변경하거나 취소하고 싶으신 경우, 플랜 변경 페이지에서 진행해주시길 바랍니다.`
                : 리뉴얼_전_플랜 === '기본 플랜'
                  ? `2025년부터 기본 플랜은 쇼핑몰 판매 관리 서비스로 변경되었습니다. 
서비스를 변경하거나 취소하고 싶으신 경우, 플랜 변경 페이지에서 진행해주시길 바랍니다.`
                  : undefined}
            </Typography>
          </VStack>

          <VStack gap={20 / 8}>
            <HStack alignItems={'center'} justifyContent={'space-between'}>
              <HStack gap={12 / 8} alignItems={'center'}>
                <Typography variant="H1_Looko">서비스 목록</Typography>
                <Typography variant="Body_Looko" color="text.secondary">
                  부가세 10% 별도
                </Typography>
              </HStack>

              <ButtonBase
                sx={{
                  height: 40,
                  width: 248,
                  px: 12 / 8,
                  borderRadius: '4px',
                  border: theme => `1px solid ${theme.palette.primary.main}`,
                }}
                LinkComponent={'a'}
                href={플랜_게스트_라우터.플랜요금_계산기.path}
                target="_blank"
              >
                <HStack flex={1} alignItems={'center'} justifyContent={'space-between'}>
                  <HStack gap={4 / 8} alignItems={'center'}>
                    <CalculateIcon sx={{ color: theme => theme.palette.primary.main }} />
                    <Typography variant="Subtitle_Looko" color="primary.main">
                      예상 요금 미리보기
                    </Typography>
                  </HStack>

                  <ChevronRight sx={{ color: theme => theme.palette.primary.main }} />
                </HStack>
              </ButtonBase>
            </HStack>

            <VStack p={3} gap={1} bgcolor={'#F5F5F5'} borderRadius={'16px'}>
              <Typography fontSize={28} fontWeight={500}>
                기본 이용료
              </Typography>

              <HStack gap={1} alignItems={'center'}>
                <Typography variant="Title_Looko">기본 서버이용료/월</Typography>
                <Typography variant="H1_Looko" color="primary.main">
                  {기본서버이용료.aimsPriceWon}
                </Typography>
                <Typography variant="Button_Looko" color="text.disabled">
                  구독 일수에 따라 일할 계산으로 요금 부과
                </Typography>
              </HStack>
            </VStack>

            <HStack gap={3}>
              {서비스_목록.map(service => {
                const isUsingService = shopInfo.servicePricingCodes?.includes(service.code);

                return (
                  <VStack
                    key={service.title}
                    gap={3}
                    flex={1}
                    p={4}
                    bgcolor={'white'}
                    borderRadius={'16px'}
                    border={isUsingService ? '3px solid #BDBDBD' : undefined}
                  >
                    <VStack flex={1} gap={52 / 8}>
                      <Typography fontSize={28} fontWeight={500}>
                        {service.title}
                      </Typography>

                      <VStack minHeight={306} gap={3}>
                        <VStack gap={12 / 8}>
                          <VStack gap={4 / 8}>
                            <Typography variant="Subtitle_Looko">상품당 요금</Typography>
                            <Typography variant="Button_Looko" color="text.disabled">
                              {service.chargingWayText}
                            </Typography>
                          </VStack>

                          <Typography variant="H1_Looko" color="primary.main">
                            {service.feeText}
                          </Typography>
                        </VStack>

                        {service.tableComponent}
                      </VStack>
                    </VStack>

                    <Divider />

                    <VStack gap={1}>
                      {service.descriptions.map((description, i) => (
                        <HStack key={i} alignItems={'center'} gap={4 / 8}>
                          <img src={CheckCircleIcon} width={16} height={16} />
                          <Typography variant="Body_Looko" color="text.secondary" whiteSpace={'pre-line'}>
                            {description}
                          </Typography>
                        </HStack>
                      ))}
                    </VStack>

                    <VStack
                      px={20 / 8}
                      height={56}
                      borderRadius={'16px'}
                      bgcolor={isUsingService ? '#BDBDBD' : 'white'}
                      border={theme => (isUsingService ? undefined : `1px solid ${theme.palette.divider}`)}
                      justifyContent={'center'}
                    >
                      {isUsingService ? (
                        <Typography fontSize={18} fontWeight={700} color="white">
                          서비스 이용중
                        </Typography>
                      ) : (
                        <Typography fontSize={18} fontWeight={700} color="text.secondary">
                          서비스 미신청
                        </Typography>
                      )}
                    </VStack>
                  </VStack>
                );
              })}
            </HStack>

            <VStack flex={1} gap={2}>
              <ButtonBase onClick={handleSubmit} sx={{ borderRadius: '16px', height: 64, bgcolor: 'primary.main' }}>
                <Typography variant="H2_Looko" color="white">
                  서비스 변경
                </Typography>
              </ButtonBase>

              <Typography variant="Body_Looko" color="text.disabled" textAlign={'center'}>
                서비스를 변경/ 취소하고 싶으시다면, 서비스 변경을 눌러주세요
              </Typography>
            </VStack>
          </VStack>
        </VStack>
      </LookoModalV2.Content>
    </LookoModalV2>
  );
});

const useH_서비스_목록 = (): Array<{
  code: 플랜_서비스_타입;
  title: string;
  chargingWayText: string;
  tableComponent: React.ReactNode;
  feeText: string;
  descriptions: string[];
}> => {
  const { AI_상품정보_자동추출_요금, AI_통합서비스_요금, 판매관리_요금 } = useH_서비스별_요금_가져오기();

  return React.useMemo(() => {
    return [
      {
        code: 'SERVICE-PRICING_CODE_SALE-PLATFORM',
        title: '쇼핑몰 판매 관리',
        chargingWayText: '각 쇼핑몰별로 게시한 상품당 요금 부과',
        tableComponent: <C쇼핑몰_판매관리_요금_테이블 />,
        feeText: `${판매관리_요금?.minFee.aimsPriceWon}~${판매관리_요금?.maxFee.aimsPriceWon}`,
        descriptions: [
          '온라인 판매 게시 및 재고 연동\n(카페24, 네이버, 세컨솔드에 한하여 재고연동)',
          '상품 주문 통합 관리',
          '엑셀 다운로드 및 관리',
          '송장번호 일괄 동시 입력',
        ],
      },
      {
        code: 'SERVICE-PRICING_CODE_AUTO-INPUT',
        title: 'AI 상품 정보 자동 추출',
        chargingWayText: '업로드한 상품당 요금 부과',
        tableComponent: <AI_상품정보_자동추출_요금_테이블 />,
        feeText: `${AI_상품정보_자동추출_요금?.minFee.aimsPriceWon}~${AI_상품정보_자동추출_요금?.maxFee.aimsPriceWon}`,
        descriptions: [
          '의류 정보 자동 입력\n(카테고리, 색상, 패턴)',
          '케어라벨 정보 자동 입력\n(브랜드, 소재, 사이즈, 원산지)',
          '검색 키워드 자동 입력',
          '가격 자동생성 시스템',
        ],
      },
      {
        code: 'SERVICE-PRICING_CODE_AI-INTEGRATION',
        title: 'AI 통합 서비스',
        chargingWayText: '업로드한 상품당 요금 부과',
        tableComponent: <AI_통합서비스_요금_테이블 />,
        feeText: `${AI_통합서비스_요금?.minFee.aimsPriceWon}~${AI_통합서비스_요금?.maxFee.aimsPriceWon}`,
        descriptions: [
          'AI 자동 배경 제거',
          'AI 디테일컷 자동 생성',
          'AI 이미지 수평 보정',
          '이미지 편집 / 포토샵 기능',
          'AI 상품 정보 자동 추출 서비스 전체',
        ],
      },
    ];
  }, [AI_상품정보_자동추출_요금, AI_통합서비스_요금, 판매관리_요금]);
};
